import "./IcebreakerCreateForm.scss";

import { Alert, Button, Divider, Flex, Text } from "@fluentui/react-northstar";
import FormState, { FieldDescriptor, FormData } from "@shopify/react-form-state";
import { SurveyLimits } from "@teams/cards";
import { randomString } from "@teams/client-shared";
import { AuthoringFormTrackingFields, OpenEndedQuestion, TeamsSurveyDraft } from "@teams/types";
import { SurveyDTO } from "@teams/types/api";
import { useCallback, useEffect, useMemo, useState } from "react";

import { useUserInfo } from "../../../../../_shared/actions/useUserInfo";
import { TextAreaField } from "../../../../../_shared/components/fields/TextAreaField";
import { PollyIcon } from "../../../../../_shared/components/PollyIcon";
import { FetchError, FetchResult, isError } from "../../../../../_shared/services/ChannelApi";
import { surveyApi } from "../../../../../_shared/services/SurveyApi";
import { templatesApi } from "../../../../../_shared/services/TemplatesApi";
import { useTeamsContext } from "../../../../../_shared/state/TeamsContextProvider";
import { getAudienceFromTeamsContext } from "../../../../../_shared/utils/Authoring";
import { clientEventTracker } from "../../../../../_shared/utils/clientTelemetry";
import { InstallWarning } from "../../../components/InstallWarning";
import { AuthoringFormHeader } from "../../../SurveyAuthoring/AuthoringFormHeader";
import { useTrackingProperties } from "../../../SurveyAuthoring/PollyAuthoring/hooks/useTrackingProperties";
import { AuthoringFormHeaderOptions } from "../../../SurveyAuthoring/PollySurveyAuthoringForm";
import { IcebreakerAiPrompt } from "./IcebreakerAiPrompt";

type Props = {
  onFormCompleted: (survey: SurveyDTO) => void;
  editSurvey?: SurveyDTO;
  duplicateSurvey?: SurveyDTO;
  trackingFields?: AuthoringFormTrackingFields;
  headerOptions?:
    | {
        showHeader?: false;
      }
    | ({
        showHeader: true;
      } & AuthoringFormHeaderOptions);
};

type FormFields = {
  icebreakerQuestion: string;
};

export const IcebreakerCreateForm: React.FC<Props> = ({
  onFormCompleted,
  editSurvey,
  duplicateSurvey,
  headerOptions,
  trackingFields,
}) => {
  const { inActiveMeeting, groupType, hostClientType, meetingType } = useTrackingProperties({
    isPersonal: trackingFields?.isPersonal ?? false,
  });

  const { teamsContext } = useTeamsContext();
  const { user } = useUserInfo();

  const [showAiPrompt, setShowAiPrompt] = useState(false);
  const [storedPrompt, setStoredPrompt] = useState<string>("");
  const [usedAiPrompt, setUsedAiPrompt] = useState<boolean>(false);

  const initialValues = useMemo(() => {
    const initialSurvey = editSurvey ?? duplicateSurvey;
    const icebreakerQuestion = initialSurvey?.questions?.[0]?.label ?? "";
    return { icebreakerQuestion };
  }, [editSurvey, duplicateSurvey]);

  const submit = async (form: FormData<FormFields>) => {
    const { fields } = form;

    const audienceItem = getAudienceFromTeamsContext(teamsContext)!;

    if (!audienceItem) {
      return [{ message: "Oops, something went wrong while creating the Icebreaker." }];
    }

    const question: OpenEndedQuestion = {
      id: randomString(),
      templateId: "",
      label: fields.icebreakerQuestion.value,
      questionType: "open-ended",
      required: true,
      allowComments: false,
      contentType: "text/plain",
    };

    const draft: TeamsSurveyDraft = {
      type: "icebreaker",
      title: fields.icebreakerQuestion.value,
      requireApproval: false,
      allowReactions: true,
      description: "",
      anonymity: "none",
      resultsVisibility: "real-time",
      commentsEnabled: false,
      deliveryPreference: "in-group",
      reminders: [],
      questions: [question],
      recipients: [
        {
          id: audienceItem.id,
          groupId: audienceItem.groupId,
          type: audienceItem.type,
          name: audienceItem.name,
        },
      ],
    };

    let result: FetchResult<{ survey: SurveyDTO }>;
    if (editSurvey?.id) {
      result = await surveyApi.updateSurvey(teamsContext, editSurvey.id, {
        ...draft,
        title: fields.icebreakerQuestion.value,
        questions: [{ ...editSurvey.questions[0], label: fields.icebreakerQuestion.value }],
        requireApproval: false,
        allowReactions: true,
      });
    } else {
      result = await surveyApi.createSurvey(teamsContext, draft, {
        authoringOrigin: trackingFields?.authoringOrigin,
        trackingSource: trackingFields?.trackingSource,
        groupType,
        hostClientType,
        inActiveMeeting,
        meetingType,
      });
    }

    if (isError(result)) {
      clientEventTracker.track("icebreaker/create-form", user, {
        error: result,
      });

      if (result instanceof FetchError && result.statusCode === 409) {
        return [{ message: "You must add the Polly app to the meeting to send Icebreakers" }];
      }

      return [result];
    }

    if (usedAiPrompt) {
      clientEventTracker.track("icebreaker edited", user, {
        value: fields.icebreakerQuestion.value !== storedPrompt,
      });
    }

    onFormCompleted(result.survey);
  };

  if (!teamsContext.meeting) {
    return <InstallWarning type="icebreaker" />;
  }

  return (
    <>
      {headerOptions?.showHeader && (
        <AuthoringFormHeader
          isEdit={headerOptions.isEdit}
          templateId={headerOptions.templateId}
          trackingSource={headerOptions.trackingSource}
        />
      )}

      {showAiPrompt ? (
        <IcebreakerAiPrompt
          onClose={question => {
            setShowAiPrompt(false);
            initialValues.icebreakerQuestion = question?.label ?? storedPrompt;

            if (question?.label) {
              setStoredPrompt(question?.label);
              setUsedAiPrompt(true);
            }
          }}
        />
      ) : (
        <>
          <Text content="Create an Icebreaker" weight="bold" size="large" />

          <FormState initialValues={initialValues} onSubmit={submit}>
            {({ fields, submit, submitting, errors }) => {
              return (
                <>
                  <IcebreakerQuestionShuffle
                    field={fields.icebreakerQuestion}
                    onPromptAiClick={() => {
                      setStoredPrompt(fields.icebreakerQuestion.value);
                      setShowAiPrompt(true);

                      clientEventTracker.track("generate with ai clicked", user, {
                        type: "icebreaker",
                      });
                    }}
                  />

                  {errors?.length > 0 &&
                    errors.map(e => <Alert key={e.message} danger content={e.message} />)}

                  <Divider />

                  <Flex hAlign="end" gap="gap.small">
                    <Button
                      content="Save"
                      onClick={submit}
                      loading={submitting}
                      disabled={submitting || !fields.icebreakerQuestion.value}
                      primary
                    />
                  </Flex>
                </>
              );
            }}
          </FormState>
        </>
      )}
    </>
  );
};

const IcebreakerQuestionShuffle = ({
  field,
  onPromptAiClick,
}: {
  field: FieldDescriptor<string>;
  onPromptAiClick: () => void;
}) => {
  const [error, setError] = useState<string>("");
  const [shuffleInProgress, setShuffleInProgress] = useState(false);

  const { teamsContext } = useTeamsContext();

  const getRandomQuestion = useCallback(async () => {
    setShuffleInProgress(true);

    field.onChange("");

    const response = await templatesApi.getRandomQuestionTemplates(teamsContext, "icebreaker", 1);
    if (isError(response)) {
      setError(response.message);
      return;
    }

    field.onChange(response.templates[0].label);

    setShuffleInProgress(false);
  }, [teamsContext, field]);

  useEffect(() => {
    if (!field.value && !shuffleInProgress) {
      getRandomQuestion();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Flex column gap="gap.smaller" className="icebreaker-create-form__shuffle">
      <Text content="Icebreaker preview" weight="bold" />
      <Flex space="between" gap="gap.smaller" vAlign="center">
        <Button text onClick={() => onPromptAiClick()} style={{ paddingLeft: 0 }}>
          <Flex gap="gap.smaller">
            <Text content="AI Generate" weight="bold" />
            <PollyIcon icon="ai-magic-icon" inheritColor />
          </Flex>
        </Button>

        <Button text onClick={() => getRandomQuestion()}>
          <Flex gap="gap.smaller">
            <Text content="Shuffle" weight="bold" />
            <PollyIcon icon="shuffle-icon" inheritColor />
          </Flex>
        </Button>
      </Flex>

      <TextAreaField
        field={field}
        maxLength={SurveyLimits.Questions.OpenEnded.Value.MaxLength}
        rows={5}
        fluid
        resize="none"
        autoFocus
      />

      {error && <Alert danger content={error} />}
    </Flex>
  );
};
